/**
 * A feed played through this deployment's video gateway.
 *
 * Tries, in order: WebRTC straight off the restream, WebRTC off the H.264
 * transcode when the server offers one, then MJPEG over the page's own HTTPS
 * connection. Each step gets a fresh one-use ticket and a few seconds to show
 * a frame. The order is cheapest and sharpest first; MJPEG is last because it
 * costs the server an encoder and the viewer most of the frame rate, and first
 * on reliability, because it needs nothing but the connection already open.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import {
  requestPlayback,
  type GatewayCapabilities,
  type PlaybackVariant,
  type VideoSource,
} from '@/api/video';

import { useFrameWatch, videoFrames, type LiveState } from './liveFrames';
import { streamMjpeg } from './mjpeg';
import { connectWhep, type WhepSession } from './whep';

/** How long one way of playing gets to show a frame before the next is tried. */
export const FIRST_FRAME_MS = 8000;
/** A picture frozen this long is reconnected with a fresh ticket. */
export const RECONNECT_AFTER_STALL_MS = 10_000;

const VARIANT_LABEL: Record<PlaybackVariant, string> = {
  direct: 'WEBRTC',
  h264: 'WEBRTC H.264',
  mjpeg: 'MJPEG',
};

export function playbackPlan(gateway: GatewayCapabilities | undefined): PlaybackVariant[] {
  return gateway?.transcode ? ['direct', 'h264', 'mjpeg'] : ['direct', 'mjpeg'];
}

const BADGE: Record<LiveState, { text: string; color: string }> = {
  live: { text: 'LIVE', color: 'var(--ok)' },
  stalled: { text: 'NO FRAMES', color: 'var(--warn)' },
  connecting: { text: 'CONNECTING', color: 'var(--muted-foreground-2)' },
  failed: { text: 'NO PICTURE', color: 'var(--danger-foreground)' },
};

export function LiveBadge({ state }: { state: LiveState }) {
  const { text, color } = BADGE[state];
  return (
    <span
      role="status"
      aria-label={`Video ${text.toLowerCase()}`}
      className="label-mono pointer-events-none absolute left-2 top-2 rounded-[99px] px-1.5 py-px text-[8px]"
      style={{ color, background: `color-mix(in oklch, ${color} 18%, rgb(0 0 0 / 0.55))` }}
    >
      {text}
    </span>
  );
}

function reason(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Connection failed.';
}

export function GatewayPlayer({ source }: { source: VideoSource }) {
  const plan = useMemo(() => playbackPlan(source.gateway), [source.gateway]);
  const [step, setStep] = useState(0);
  const [round, setRound] = useState(0);
  const [reasons, setReasons] = useState<string[]>([]);
  const [waiting, setWaiting] = useState(false);
  const [frameUrl, setFrameUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const counter = useRef<() => number | null>(() => null);

  const variant = plan[step] ?? null;
  const state = useFrameWatch(() => counter.current(), {
    failed: variant === null,
    waiting,
    resetKey: `${variant}-${round}`,
  });

  useEffect(() => {
    setStep(0);
    setReasons([]);
  }, [source.droneId, source.url]);

  useEffect(() => {
    if (variant === null) return;
    let cancelled = false;
    let session: WhepSession | null = null;
    let shown: string | null = null;
    let mjpegFrames = 0;
    const abort = new AbortController();
    counter.current = () => null;

    const giveUp = (why: string) => {
      if (cancelled) return;
      cancelled = true;
      setReasons((previous) => [...previous, `${VARIANT_LABEL[variant]}: ${why}`]);
      setStep((previous) => previous + 1);
    };

    void (async () => {
      const playback = await requestPlayback(source.droneId, variant);
      if (cancelled) return;
      if (!playback.ok) {
        giveUp(playback.error.message);
        return;
      }
      try {
        if (variant === 'mjpeg') {
          counter.current = () => mjpegFrames;
          await streamMjpeg(
            playback.data.url,
            playback.data.ticket,
            (frame) => {
              mjpegFrames += 1;
              const next = URL.createObjectURL(frame);
              setFrameUrl(next);
              if (shown) URL.revokeObjectURL(shown);
              shown = next;
            },
            abort.signal,
          );
          // The relay ends a stream on a timer, and a gateway restart ends it
          // early. Either way the viewer wanted a picture: ask again.
          if (!cancelled) setRound((previous) => previous + 1);
          return;
        }
        const video = videoRef.current;
        if (!video) return;
        const opened = await connectWhep(playback.data.url, video, {
          ticket: playback.data.ticket,
        });
        if (cancelled) {
          opened.close();
          return;
        }
        session = opened;
        counter.current = () => opened.framesDecoded() ?? videoFrames(video);
      } catch (error) {
        if (!abort.signal.aborted) giveUp(reason(error));
      }
    })();

    const firstFrame = setTimeout(() => {
      if (!counter.current()) giveUp('no picture arrived');
    }, FIRST_FRAME_MS);

    return () => {
      cancelled = true;
      clearTimeout(firstFrame);
      abort.abort();
      session?.close();
      if (shown) URL.revokeObjectURL(shown);
    };
  }, [source.droneId, source.url, variant, round]);

  useEffect(() => {
    if (state !== 'stalled') return;
    const timer = setTimeout(() => setRound((previous) => previous + 1), RECONNECT_AFTER_STALL_MS);
    return () => clearTimeout(timer);
  }, [state]);

  if (variant === null) {
    return (
      <div className="well flex flex-col gap-1.5 rounded-[11px] px-3 py-2.5">
        <span className="label-mono text-[8px]" style={{ color: 'var(--danger-foreground)' }}>
          NO PICTURE
        </span>
        <ul className="flex flex-col gap-0.5 text-[11px] leading-snug text-[color:var(--muted-foreground-2)]">
          {reasons.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => {
            setReasons([]);
            setStep(0);
            setRound((previous) => previous + 1);
          }}
          className="well self-start rounded-[99px] px-2 py-1 label-mono text-[9px]"
        >
          TRY AGAIN
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      {variant === 'mjpeg' ? (
        frameUrl ? (
          <img
            src={frameUrl}
            alt={`${source.label} video`}
            className="w-full rounded-[8px] bg-black"
          />
        ) : (
          <div
            aria-label={`${source.label} video`}
            className="aspect-video w-full rounded-[8px] bg-black"
          />
        )
      ) : (
        <video
          ref={videoRef}
          className="w-full rounded-[8px] bg-black"
          autoPlay
          muted
          playsInline
          aria-label={`${source.label} video`}
          onWaiting={() => setWaiting(true)}
          onStalled={() => setWaiting(true)}
          onPlaying={() => setWaiting(false)}
        />
      )}
      <LiveBadge state={state} />
      <span className="label-mono pointer-events-none absolute right-2 top-2 rounded-[99px] bg-black/55 px-1.5 py-px text-[8px] text-[color:var(--muted-foreground-2)]">
        {VARIANT_LABEL[variant]}
      </span>
    </div>
  );
}
